import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useNotifications } from "@/context/NotificationsContext";
import { PageHeader, Card, Loading, EmptyState, Btn } from "@/components/primitives";
import { StatusBadge } from "@/components/StatusBadge";
import { FleetMap } from "@/components/FleetMap";
import { fmtDate, initials } from "@/lib/format";
import { geocode, routeEta, destForCall, mapsFromTo } from "@/lib/routing";
import { Radio, MapPin, Navigation, RefreshCw, Truck, User, Clock } from "lucide-react";

const ACTIVE = ["dispatched", "accepted", "on_scene", "transporting", "delivered"];

export default function Fleet() {
  const [data, setData] = useState(null);
  const [etas, setEtas] = useState({}); // callId -> {durationMin, distanceMi, dest:{lat,lng}, label, address}
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const etaKeyRef = useRef("");
  const { subscribe } = useNotifications();
  const lastRt = useRef(0);

  const load = useCallback(async (silent) => {
    if (!silent) setRefreshing(true);
    try {
      const r = await api.get("/fleet");
      setData(r.data);
    } catch (e) {
      setData({ calls: [], drivers: [] });
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 60000); // safety-net; WS drives real-time
    const onVis = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  // Real-time fleet & calls via WebSocket (call.* + fleet.location), throttled refetch.
  useEffect(() => {
    const off = subscribe((evt) => {
      if (["call.new", "call.updated", "call.deleted", "fleet.location"].includes(evt.type)) {
        const now = Date.now();
        if (now - lastRt.current > 2500) { lastRt.current = now; load(true); }
      }
    });
    return off;
  }, [subscribe, load]);

  // Merge live driver GPS (from /fleet `drivers[]`, keyed by active_call_id) into
  // each call's `last` — the driver's real-time ping isn't on the call object itself.
  const active = useMemo(() => {
    const calls = (data?.calls || []).filter((c) => ACTIVE.includes(c.status));
    const drivers = data?.drivers || [];
    return calls.map((c) => {
      if (c.last && c.last.lat != null) return c;
      const drv = drivers.find((d) => d.active_call_id === c.id && d.lat != null && d.lng != null);
      return drv ? { ...c, last: { lat: drv.lat, lng: drv.lng }, driver_seen_at: drv.seen_at } : c;
    });
  }, [data]);

  // Compute live ETA for active calls that have a driver GPS ping. Geocode is cached.
  useEffect(() => {
    const withGps = active.filter((c) => c.last && c.last.lat != null);
    const key = withGps.map((c) => `${c.id}:${c.last.lat.toFixed(4)},${c.last.lng.toFixed(4)}:${c.status}`).join("|");
    if (key === etaKeyRef.current) return;
    etaKeyRef.current = key;
    let cancelled = false;
    (async () => {
      const next = {};
      for (const c of withGps) {
        const { address, label } = destForCall(c);
        if (!address) continue;
        const dest = await geocode(address);
        if (!dest) continue;
        const eta = await routeEta(c.last, dest);
        next[c.id] = { ...eta, dest, label, address };
      }
      if (!cancelled) setEtas(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.map((c) => c.id + (c.last ? `${c.last.lat},${c.last.lng}` : "") + c.status).join("|")]);

  // Drivers who are online (recent ping) but NOT on a rendered active call —
  // shown as grey "available" pins so dispatch can see who's free to assign.
  const availableDrivers = useMemo(() => {
    const activeIds = new Set(active.map((c) => c.id));
    return (data?.drivers || []).filter((d) => d.lat != null && d.lng != null && !(d.active_call_id && activeIds.has(d.active_call_id)));
  }, [data, active]);

  if (data === null) return <Loading />;

  return (
    <div>
      <PageHeader title="Live Fleet" subtitle="Real-time board of active transports, driver locations & ETAs.">
        <Btn variant="secondary" onClick={() => load()} data-testid="fleet-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Btn>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-xs text-curtron-muted">
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-curtron-info animate-pulse" /> Auto-refreshing every 20s · ETAs via OpenStreetMap</span>
        {availableDrivers.length > 0 && <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-curtron-muted" /> {availableDrivers.length} available driver{availableDrivers.length !== 1 ? "s" : ""}</span>}
      </div>

      <div className="mb-4">
        <FleetMap transports={active} etas={etas} availableDrivers={availableDrivers} />
      </div>

      {active.length === 0 ? (
        <Card><EmptyState icon={Radio} title="No active transports" subtitle="Active transports with live tracking will appear here." /></Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {active.map((c) => {
            const eta = etas[c.id];
            const { address, label } = destForCall(c);
            return (
              <Card key={c.id} className="p-4 hover:shadow-sm transition-shadow" data-testid={`fleet-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-curtron-ink truncate">{c.deceased_name || "—"}</div>
                    <div className="text-xs text-curtron-muted truncate">{c.agency_for || "—"}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>

                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-start gap-2 text-curtron-sub"><MapPin className="w-4 h-4 mt-0.5 text-curtron-moss shrink-0" /><span className="truncate">{c.pickup_location || "—"}</span></div>
                  <div className="flex items-start gap-2 text-curtron-sub"><MapPin className="w-4 h-4 mt-0.5 text-curtron-terracotta shrink-0" /><span className="truncate">{c.dropoff_location || "—"}</span></div>
                </div>

                {c.last && c.last.lat != null && (
                  <div className="mt-3 flex items-center gap-2 rounded-md bg-curtron-bg px-2.5 py-2" data-testid={`fleet-eta-${c.id}`}>
                    <Clock className="w-4 h-4 text-curtron-green shrink-0" />
                    <span className="text-sm text-curtron-ink">
                      {eta && eta.durationMin != null
                        ? <><span className="font-semibold">~{eta.durationMin} min</span> to {label}{eta.distanceMi != null ? ` · ${eta.distanceMi} mi` : ""}</>
                        : <span className="text-curtron-muted">Calculating ETA to {label}…</span>}
                    </span>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2 pt-3 border-t border-curtron-line">
                  <div className="w-7 h-7 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-[11px] font-semibold">
                    {c.driver_name && c.driver_name !== "—" ? initials(c.driver_name) : <User className="w-3.5 h-3.5" />}
                  </div>
                  <div className="text-sm text-curtron-ink flex-1 truncate">{c.driver_name && c.driver_name !== "—" ? c.driver_name : "Unassigned"}</div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <a href={mapsFromTo(c.last, address)} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid={`fleet-navigate-${c.id}`}>
                    <Btn variant="secondary" className="w-full"><Navigation className="w-4 h-4" /> Navigate {c.last && c.last.lat != null ? "from driver" : "to " + label}</Btn>
                  </a>
                  <Btn variant="ghost" onClick={() => navigate(`/transports/${c.id}`)} data-testid={`fleet-detail-${c.id}`}><Truck className="w-4 h-4" /></Btn>
                </div>
                <div className="text-[11px] text-curtron-muted mt-2">Scheduled {fmtDate(c.scheduled_at)}</div>
              </Card>
            );
          })}
        </div>
      )}

      {availableDrivers.length > 0 && (
        <div className="mt-5">
          <h2 className="font-heading font-semibold text-curtron-ink text-sm mb-2 flex items-center gap-2"><User className="w-4 h-4 text-curtron-muted" /> Available drivers</h2>
          <Card className="divide-y divide-curtron-line">
            {availableDrivers.map((d) => (
              <div key={d.id} className="px-4 py-3 flex items-center gap-3" data-testid={`available-driver-${d.id}`}>
                <span className="w-2.5 h-2.5 rounded-full bg-curtron-muted shrink-0" />
                <div className="w-8 h-8 rounded-full bg-curtron-bg text-curtron-sub flex items-center justify-center text-[11px] font-semibold">{d.name ? initials(d.name) : <User className="w-4 h-4" />}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-curtron-ink truncate">{d.name || "Driver"}</div>
                  <div className="text-[11px] text-curtron-muted">Online · last seen {d.seen_at ? fmtDate(d.seen_at) : "—"}</div>
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${d.lat},${d.lng}`} target="_blank" rel="noopener noreferrer">
                  <Btn variant="ghost" className="h-8"><MapPin className="w-4 h-4" /></Btn>
                </a>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
